import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap, Mail, Lock, User, ArrowRight, Check, Star, DollarSign, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const from = location.state?.from?.pathname || "/";

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      const { access_token, user } = response.data;

      login(user, access_token);
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex flex-1 bg-black p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-16">
            <div className="w-12 h-12 bg-[#CCFF00] flex items-center justify-center">
              <Zap className="w-7 h-7 text-black" />
            </div>
            <span className="text-white font-['Syne'] text-2xl font-extrabold tracking-tighter">
              TRENDI
            </span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-white text-5xl font-extrabold tracking-tighter leading-tight mb-6">
              Discover Trends.<br />
              Analyze Data.<br />
              Earn Money.
            </h1>
            <p className="text-gray-400 text-lg max-w-md mb-8">
              Join 50,000+ trend hunters discovering viral opportunities and earning through our affiliate program.
            </p>

            {/* Benefits List */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-white">
                <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-black" />
                </div>
                <span>Real-time trend analytics</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-8 h-8 bg-[#FF0099] flex items-center justify-center">
                  <Star className="w-4 h-4 text-white" />
                </div>
                <span>Track 14+ platforms</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-8 h-8 bg-[#7C3AED] flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-white" />
                </div>
                <span>Earn 10% affiliate commission</span>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="flex gap-4">
          {["TikTok", "YouTube", "Instagram", "Etsy", "Shopify"].map((platform) => (
            <div
              key={platform}
              className="px-3 py-1 border border-gray-700 text-gray-400 text-xs font-mono uppercase"
            >
              {platform}
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-black flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#CCFF00]" />
            </div>
            <span className="font-['Syne'] text-xl font-extrabold tracking-tighter">
              TRENDI
            </span>
          </div>

          <h2 className="text-3xl font-extrabold tracking-tighter mb-2">
            {isLogin ? "Welcome back" : "Join Trendi Free"}
          </h2>
          <p className="text-gray-500 mb-8">
            {isLogin
              ? "Sign in to continue to Trendi"
              : "Create your account and start trending"}
          </p>

          {/* Free Benefits (Register only) */}
          {!isLogin && (
            <div className="mb-6 p-4 bg-[#CCFF00]/20 border-2 border-[#CCFF00]">
              <div className="text-sm font-bold mb-2">Free account includes:</div>
              <ul className="text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" /> Browse all trending topics
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" /> Save unlimited trends
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" /> Basic analytics access
                </li>
              </ul>
            </div>
          )}

          {/* Google OAuth Button */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full mb-6 bg-white text-black border-2 border-black hover:bg-gray-50 font-bold py-3 flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-gray-400 text-sm font-mono uppercase">
              or
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-sm font-bold uppercase tracking-wide">
                  Name
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    data-testid="name-input"
                    placeholder="Your name"
                    className="pl-10 border-2 border-black focus:ring-0 focus:border-[#7C3AED]"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-bold uppercase tracking-wide">
                Email
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                  placeholder="you@example.com"
                  className="pl-10 border-2 border-black focus:ring-0 focus:border-[#7C3AED]"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-bold uppercase tracking-wide">
                Password
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="password-input"
                  placeholder="••••••••"
                  className="pl-10 border-2 border-black focus:ring-0 focus:border-[#7C3AED]"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full bg-black text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold py-3 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent animate-spin rounded-full" />
              ) : (
                <>
                  {isLogin ? "Sign In" : "Create Account"}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          {/* Toggle Auth Mode */}
          <p className="text-center mt-6 text-gray-500">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="font-bold text-black hover:text-[#FF0099] transition-colors"
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
